import {DocumentIcon, DocumentTextIcon, EllipsisVerticalIcon, ReceiptPercentIcon} from '@heroicons/react/24/outline';
import {CreditCardStatementIcon} from '../../../../../components/general/CustomIcons/CreditCardStatementIcon';
import {
    createTable,
    RenderingPresetType,
    SortingState as TableSortingState,
    TableHeaderSpec
} from '../../../../../components/table/CreateTable';
import {memo, useCallback, useMemo, useState} from 'react';
import {cleanDocumentFileName} from '../../../../../lib/TFICLib';
import DocumentFilterBar from './DocumentFilterBar';
import {DocumentTableProps, PartnerBill, PartnerBillOrCreditCardStatement, getDeleteBlockedReason, isCreditCardStatement} from './partnerDocuments';
import {
    DOCUMENT_DIRECTION_LABELS,
    DocumentTagEnum,
} from '../../../../../types/enums';
import {InfoTooltip} from '../../../../../components/general/InfoTooltip';
import {Tag} from '../../../../../components/general/Tag';
import VATAmountWithToggle from "./VATAmountWithToggle.tsx";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import Popup from '../../../../../components/general/Popup.tsx';
import TableTags from './components/TableTags.tsx';
import type {DocumentCategory} from '../../../../../generated/enums.ts';
import {ForeignAmountLabel} from '../../../../../components/general/ForeignAmountLabel.tsx';
import type {Money} from '../../../../../lib/Money.ts';

const INVOICE_AND_RECEIPT_CATEGORIES: DocumentCategory[] = [
    'invoice',
    'pos_receipt',
];

function isInvoiceOrReceipt(doc: PartnerBillOrCreditCardStatement): doc is PartnerBill {
    return !isCreditCardStatement(doc) && INVOICE_AND_RECEIPT_CATEGORIES.includes(doc.document_category);
}

function getDocumentIcon(category: DocumentCategory) {
    switch (category) {
        case 'invoice':
            return DocumentTextIcon;
        case 'pos_receipt':
            return ReceiptPercentIcon;
        case 'credit_card_statement':
            return CreditCardStatementIcon;
        case 'other':
            return DocumentIcon;
        default:
            return DocumentIcon;
    }
}

function getVatValue(doc: PartnerBill) {
    if (!doc.scanned_fields?.vat_amount_overall?.signed_amount) return null;

    const reverseChargeApplied = !!doc.scanned_fields?.vat_reverse_charge_applied;
    const signed = Number(doc.scanned_fields.vat_amount_overall.signed_amount);
    const value = reverseChargeApplied ? 0 : signed;
    return value / 100;
}

export function deriveTags(doc: PartnerBillOrCreditCardStatement, canSeeFixedAssets = false) {
    const tags: DocumentTagEnum[] = [];
    if (isCreditCardStatement(doc)) return tags;
    if (doc.is_matched) tags.push(DocumentTagEnum.MATCHED);
    if (doc.matching_exclusion_reason) tags.push(DocumentTagEnum.PRIVATELY_PAID);
    if (doc.needs_write_off) tags.push(DocumentTagEnum.WRITE_OFF);
    if (canSeeFixedAssets && doc.fixed_asset_id) tags.push(DocumentTagEnum.FIXED_ASSET);
    return tags;
}

interface DocumentActionsMenuProps {
    doc: PartnerBillOrCreditCardStatement;
    onDeleteClick: (doc: PartnerBillOrCreditCardStatement) => void;
    onMarkAsPrivatelyPaid?: (bill: PartnerBill) => void | Promise<void>;
}

function DocumentActionsMenu({doc, onDeleteClick, onMarkAsPrivatelyPaid}: DocumentActionsMenuProps) {
    const deleteBlockedReason = getDeleteBlockedReason(doc);
    const isDeleteDisabled = deleteBlockedReason !== null;
    const canMarkAsPrivatelyPaid =
        !!onMarkAsPrivatelyPaid &&
        !isCreditCardStatement(doc) &&
        !doc.is_matched &&
        !doc.matching_exclusion_reason;

    return (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <Menu as="div" className="relative inline-block text-left">
                <MenuButton 
                    className="p-2 hover:bg-gray-100 rounded-full"
                    onClick={(e) => e.stopPropagation()}
                >
                    <EllipsisVerticalIcon className="h-5 w-5 text-gray-600" aria-hidden="true"/>
                </MenuButton>
                <MenuItems
                    anchor={{ to: 'bottom end', gap: 2 }}
                    transition
                    className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                    {canMarkAsPrivatelyPaid && (
                        <MenuItem as="div" className="relative flex items-center">
                            {({ focus }) => (
                                <button
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!onMarkAsPrivatelyPaid || isCreditCardStatement(doc)) return;
                                        await onMarkAsPrivatelyPaid(doc);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    }`}
                                >
                                    Privé betaald
                                </button>
                            )}
                        </MenuItem>
                    )}
                    <MenuItem as="div" className="relative flex items-center">
                        {() => (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isDeleteDisabled) {
                                        onDeleteClick(doc);
                                    }
                                }}
                                disabled={isDeleteDisabled}
                                className={`w-full text-left px-4 py-2 text-sm ${
                                    isDeleteDisabled
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-red-600 hover:bg-red-50'
                                }`}
                                title={deleteBlockedReason ?? undefined}
                            >
                                Verwijder
                            </button>
                        )}
                    </MenuItem>
                </MenuItems>
            </Menu>
        </div>
    );
}

interface ExtendedDocumentTableProps extends DocumentTableProps {
    onVatStatusChange: (bill: PartnerBill) => void;
    onRowClick?: (documentId: string, businessId: string) => void;
    onDelete?: (document: PartnerBillOrCreditCardStatement) => Promise<void>;
    onMarkAsPrivatelyPaid?: (bill: PartnerBill) => Promise<void>;
    onUndoPrivatelyPaid?: (bill: PartnerBill) => Promise<void>;
    canSeeFixedAssets?: boolean;
    onFixedAssetClick?: (bill: PartnerBill) => void;
    isLoading?: boolean;
}

const ClientSharedDocumentsTable = ({
                                        documents,
                                        sortingState,
                                        onSortingChange,
                                        filterOptions,
                                        onVatStatusChange,
                                        onRowClick,
                                        onDelete,
                                        onMarkAsPrivatelyPaid,
                                        onUndoPrivatelyPaid,
                                        canSeeFixedAssets = false,
                                        onFixedAssetClick,
                                        isLoading,
                                    }: ExtendedDocumentTableProps) => {

    const availableRelations = filterOptions.relations;
    const clientDocuments = documents;

    const availableTags = [
        {name: 'Match', id: DocumentTagEnum.MATCHED},
        {name: 'Privé betaald', id: DocumentTagEnum.PRIVATELY_PAID},
        {name: 'Afschrijven', id: DocumentTagEnum.WRITE_OFF},
        ...(canSeeFixedAssets ? [{name: 'Activum', id: DocumentTagEnum.FIXED_ASSET}] : []),
    ];

    const [currentSortingState, setCurrentSortingState] = useState<TableSortingState | null>(
        sortingState.orderby ? {
            orderBy: sortingState.orderby,
            orderDescending: sortingState.order === 'desc'
        } : null
    );
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<PartnerBillOrCreditCardStatement | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteClick = useCallback((doc: PartnerBillOrCreditCardStatement) => {
       setDeleteConfirmationOpen(doc);
    }, []);

    const handleDeleteConfirm = async () => {
        if (deleteConfirmationOpen && onDelete) {
            setIsDeleting(true);
            try {
                await onDelete(deleteConfirmationOpen);
            } finally {
                setIsDeleting(false);
                setDeleteConfirmationOpen(null);
            }
        }
    };

    const tableSpec = useMemo<TableHeaderSpec<PartnerBillOrCreditCardStatement>>(() => ({
        title: `${clientDocuments.length} Documenten`,
        isLoading: isLoading,
        sorting: {
            sortingState: [currentSortingState, setCurrentSortingState],
            inMemory: false
        },
        onQueryUpdate: (pagination, sorting) => {
            onSortingChange({orderby: sorting.orderBy, order: sorting.orderDescending ? 'desc' : 'asc'});
        },
        columns: [
            {
                name: 'Document',
                field: 'file_name',
                disableSorting: true,
                style: {
                    widthMultiplier: 1.5,
                    skeleton: 'lead'
                },
                customRender: (doc: PartnerBillOrCreditCardStatement) => {
                    const IconComponent = getDocumentIcon(doc.document_category);
                    return (
                        <div className="flex items-center">
                            <div className="h-14 w-14rounded-full mr-4 flex-shrink-0 flex items-center justify-center">
                                <IconComponent className="h-8 w-8 text-gray-400" aria-hidden="true"/>
                            </div>
                            <div>
                                {doc.business_name && (
                                    <div className="text-xs text-gray-600 font-medium">
                                        {doc.business_name}
                                        <Tag
                                            key={doc.document_direction}
                                            variant={`secondary`}
                                            removable={false}
                                            size={`xxs`}
                                            className="ml-2"
                                        >
                                            {DOCUMENT_DIRECTION_LABELS[doc.document_direction]}
                                        </Tag>
                                    </div>
                                )}
                                <div className="text-sm text-gray-900 mt-1">
                                    {cleanDocumentFileName(doc.file_name)}
                                </div>
                            </div>
                        </div>
                    );
                }
            },
            {
                name: 'Relatie',
                field: 'relation_name',
                style: {
                    widthMultiplier: 1.5
                }
            },
            {
                name: 'Factuurnummer',
                field: 'scanned_fields.reference_identifier',
                style: {
                    widthMultiplier: 0.5
                },
            },
            {
                name: 'Factuurdatum',
                field: 'scanned_fields.normalized_date',
                renderingPreset: RenderingPresetType.Date,
                style: {
                    widthMultiplier: 0.5
                }
            },
            {
                name: 'Uploaddatum',
                field: 'created_at',
                renderingPreset: RenderingPresetType.Date,
                style: {
                    widthMultiplier: 0.5
                }
            },
            {
                name: 'Bedrag',
                field: 'total_amount_payable',
                customRender: (doc: PartnerBillOrCreditCardStatement) => {
                    if (!isInvoiceOrReceipt(doc)) {
                        return null;
                    }
                    const amount = doc.total_amount_payable;
                    return (
                        <ForeignAmountLabel
                            amount={{signed_amount: amount, currency: doc.currency} as Money}
                            origin={doc.monetary_origin ?? null}
                            colorAid={true}
                        />
                    );
                },
                style: {
                    widthMultiplier: 0.5,
                    align: 'right'
                }
            },
            {
                name: 'Tags',
                field: '__tags__',
                customRender: (doc: PartnerBillOrCreditCardStatement) => {
                    const tags = deriveTags(doc, canSeeFixedAssets);
                    return (
                        <TableTags
                            tags={tags}
                            onUndoPrivatelyPaid={
                                onUndoPrivatelyPaid && !isCreditCardStatement(doc) ? () => onUndoPrivatelyPaid(doc) : undefined
                            }
                            onFixedAssetClick={
                                onFixedAssetClick && !isCreditCardStatement(doc) ? () => onFixedAssetClick(doc) : undefined
                            }
                        />
                    );
                },
                style: {
                    widthMultiplier: 0.9
                }
            },
            {
                name: <>BTW <InfoTooltip className="max-w-64"
                                         text="Wanneer de btw voor een document is aangevinkt, wordt de btw op dat document gebruikt voor de berekening van de realtime btw-indicatie. Je kunt de btw voor een document uitschakelen als het bijvoorbeeld privé is, niet-aftrekbaar, of als de factuur niet correct is. De btw telt dan niet mee voor de btw-berekeningen en de aangifte."/></>,
                field: 'is_vat_ignored',
                customRender: (doc: PartnerBillOrCreditCardStatement) => {
                    if (!isInvoiceOrReceipt(doc)) {
                        return null;
                    }
                    return (
                        <VATAmountWithToggle
                            onChange={() => onVatStatusChange(doc)}
                            amount={getVatValue(doc)}
                            toggleState={!doc.is_vat_ignored}
                        />
                    );
                },
                style: {
                    widthMultiplier: 0.5
                }
            },
            {
                name: '',
                field: '__actions__',
                disableSorting: true,
                style: {
                    widthMultiplier: 0.3
                },
                customRender: (doc: PartnerBillOrCreditCardStatement) => (
                    <DocumentActionsMenu
                        doc={doc}
                        onDeleteClick={handleDeleteClick}
                        onMarkAsPrivatelyPaid={onMarkAsPrivatelyPaid}
                    />
                )
            }
            
          
        ],
        onEmptyRender: () => <>
            <p className="text-gray-500 pt-12 text-center">
                Geen documenten, pas de filters aan om meer documenten te zien.
            </p>
        </>,
        onRowClick: (row) => {
            if (onRowClick) onRowClick(row.id, row.business_id);
        },
        options: {
            disableTopPaginationWidget: false,
            disableBottomPaginationWidget: true,
            hideTableHeader: false,
            allowFullRowClick: true
        }
    }), [clientDocuments.length, currentSortingState, onSortingChange, onVatStatusChange, handleDeleteClick, onMarkAsPrivatelyPaid, onUndoPrivatelyPaid, canSeeFixedAssets, onFixedAssetClick, onRowClick, isLoading]);

    const TableComponent = createTable<PartnerBillOrCreditCardStatement>(clientDocuments, tableSpec);

    return (
        <>
            <DocumentFilterBar
                availableRelations={availableRelations}
                availableTags={availableTags}
            />
            {TableComponent()}
            <Popup
                open={deleteConfirmationOpen}
                onClose={() => setDeleteConfirmationOpen(null)}
                onConfirm={handleDeleteConfirm}
                title="Document verwijderen"
                variant="danger"
                confirmButtonDisplayLabel="Verwijderen"
                confirmButtonDisabled={isDeleting}
            >
                <p className="text-sm text-gray-700">
                    Weet je zeker dat je dit document wilt verwijderen?
                </p>
            </Popup>
        </>
    );
};

export default memo(ClientSharedDocumentsTable);



